import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { BookOpen, Printer, ExternalLink, Search, ArrowUp, Sparkles } from "lucide-react";

/**
 * In-app training manual reader.
 *
 * Fetches the raw markdown from /api/help/manual, renders it with
 * react-markdown, and offers:
 *   - Sticky right-hand TOC autogenerated from the H2 headings
 *   - Section anchor links (deep-link-friendly)
 *   - "Open printable version" button that opens /api/help/manual.html in a
 *     new tab (that page has its own Print/Save-as-PDF button)
 *   - In-page search filter
 */
export default function Help() {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [activeSlug, setActiveSlug] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/help/manual");
        setMarkdown(data.markdown || "");
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Extract H2 sections for the TOC.
  const toc = useMemo(() => {
    const lines = markdown.split("\n");
    const out = [];
    for (const line of lines) {
      const m = /^## +(.+?)\s*$/.exec(line);
      if (m) {
        const label = m[1].replace(/^\d+\.\s*/, "").trim();
        const slug = slugify(m[1]);
        out.push({ label, slug, raw: m[1] });
      }
    }
    return out;
  }, [markdown]);

  const filteredMarkdown = useMemo(() => {
    if (!filter.trim()) return markdown;
    const q = filter.trim().toLowerCase();
    // Split by H2, keep sections that match
    const blocks = markdown.split(/\n(?=## )/g);
    const kept = blocks.filter((b) => b.toLowerCase().includes(q));
    return kept.length ? kept.join("\n") : "_No sections match your search._";
  }, [markdown, filter]);

  // Scrollspy — highlight the current section in the TOC while scrolling.
  useEffect(() => {
    if (!markdown) return;
    const handler = () => {
      const els = document.querySelectorAll("h2[id]");
      let current = "";
      for (const el of els) {
        if (el.getBoundingClientRect().top < 120) current = el.id;
      }
      setActiveSlug(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [markdown, filteredMarkdown]);

  const openPrintable = async () => {
    try {
      // Fetch the HTML via axios (adds auth), then open as a blob URL.
      const { data } = await api.get("/help/manual.html", { responseType: "text" });
      const blob = new Blob([data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="help-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> Operator Training Manual
          </div>
          <h1 className="text-3xl font-bold text-neutral-900">Help &amp; how-to</h1>
          <p className="text-neutral-600 mt-2 text-sm max-w-2xl">
            Every workflow in the console — from finding a plumber with no website
            to countersigning their contract. Also includes the full cold-call sales
            script and objection handling.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/help/roleplay"
            data-testid="help-roleplay-cta"
            className="bg-neutral-900 text-white hover:bg-amber-500 hover:text-neutral-900 text-sm px-4 py-2 flex items-center gap-2 font-semibold transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Practice cold-call now
          </Link>
          <button
            onClick={openPrintable}
            data-testid="help-print-btn"
            className="lf-btn-outline text-sm flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> Printable / PDF view
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-neutral-400">Loading manual…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-10">
          {/* Content */}
          <article
            className="prose prose-neutral max-w-none help-prose"
            data-testid="help-content"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: (props) => <h1 className="text-4xl font-bold text-neutral-900 mb-2" {...props} />,
                h2: ({ children, ...props }) => {
                  const text = flattenChildren(children);
                  const slug = slugify(text);
                  return (
                    <h2
                      id={slug}
                      className="text-2xl font-bold text-neutral-900 mt-14 pt-6 border-t-2 border-neutral-900 scroll-mt-24"
                      {...props}
                    >
                      {children}
                    </h2>
                  );
                },
                h3: (props) => (
                  <h3 className="text-lg font-bold text-neutral-900 mt-8 mb-2" {...props} />
                ),
                h4: (props) => (
                  <h4 className="text-xs font-mono uppercase tracking-widest text-neutral-500 mt-6 mb-2" {...props} />
                ),
                p: (props) => <p className="text-neutral-700 leading-relaxed mb-3" {...props} />,
                a: ({ href, ...props }) => {
                  // Convert internal anchors (#section-name) into smooth-scroll links
                  if (href && href.startsWith("#")) {
                    return (
                      <a
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(href.slice(1));
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                        className="underline text-neutral-900 hover:text-amber-700"
                        {...props}
                      />
                    );
                  }
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="underline text-neutral-900 hover:text-amber-700 inline-flex items-center gap-1"
                      {...props}
                    />
                  );
                },
                ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-1 text-neutral-700" {...props} />,
                ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-1 text-neutral-700" {...props} />,
                li: (props) => <li className="leading-relaxed" {...props} />,
                blockquote: (props) => (
                  <blockquote
                    className="border-l-4 border-amber-400 bg-amber-50 px-5 py-3 my-4 text-neutral-800"
                    {...props}
                  />
                ),
                code: ({ inline, ...props }) =>
                  inline ? (
                    <code className="bg-neutral-100 px-1.5 py-0.5 text-[13px] font-mono text-neutral-900" {...props} />
                  ) : (
                    <code className="block bg-neutral-100 p-4 text-[13px] font-mono text-neutral-900 overflow-x-auto border-l-2 border-amber-400" {...props} />
                  ),
                table: (props) => (
                  <div className="overflow-x-auto my-5">
                    <table className="w-full text-sm border-collapse" {...props} />
                  </div>
                ),
                th: (props) => (
                  <th className="bg-neutral-900 text-white text-left px-3 py-2 font-mono text-xs uppercase tracking-widest border border-neutral-900" {...props} />
                ),
                td: (props) => (
                  <td className="border border-neutral-200 px-3 py-2 align-top text-neutral-700" {...props} />
                ),
                hr: () => <hr className="my-10 border-neutral-200" />,
                strong: (props) => <strong className="font-bold text-neutral-900" {...props} />,
              }}
            >
              {filteredMarkdown}
            </ReactMarkdown>
          </article>

          {/* TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-6">
              <div className="mb-3">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    placeholder="Search sections…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full text-xs border border-neutral-300 pl-8 pr-3 py-2 focus:outline-none focus:border-neutral-900"
                    data-testid="help-search-input"
                  />
                </div>
              </div>
              <div className="overline mb-3">On this page</div>
              <nav className="flex flex-col gap-1 max-h-[70vh] overflow-y-auto pr-2" data-testid="help-toc">
                {toc.map((t) => (
                  <a
                    key={t.slug}
                    href={`#${t.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(t.slug);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className={`text-xs leading-snug py-1.5 pl-3 border-l-2 transition-colors ${
                      activeSlug === t.slug
                        ? "border-amber-400 text-neutral-900 font-semibold"
                        : "border-transparent text-neutral-500 hover:text-neutral-900"
                    }`}
                    data-testid={`help-toc-${t.slug}`}
                  >
                    {t.label}
                  </a>
                ))}
              </nav>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="mt-6 text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-neutral-900 flex items-center gap-1"
                data-testid="help-back-to-top"
              >
                <ArrowUp className="w-3 h-3" /> Back to top
              </button>
              <a
                href="/api/help/manual.html"
                onClick={(e) => { e.preventDefault(); openPrintable(); }}
                className="mt-3 text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1"
                data-testid="help-printable-link"
              >
                <ExternalLink className="w-3 h-3" /> Open printable version
              </a>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

/** Convert "1. Platform Overview" → "1-platform-overview" (GitHub-style). */
function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

/** Recursively flatten React children into a plain string. */
function flattenChildren(children) {
  if (children == null) return "";
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(flattenChildren).join("");
  if (typeof children === "object" && children.props) {
    return flattenChildren(children.props.children);
  }
  return "";
}
